import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, AlignLeft, ArrowLeft, Calendar, CheckCircle2, ExternalLink, Pause, Play, RefreshCw, Repeat, Target, Trash2, Undo2, Wrench, XCircle } from 'lucide-react'
import { useProvideFeedbackContext } from '../components/feedback/FeedbackContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ActionMenu } from '../components/ui/ActionMenu'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Timestamp } from '../components/ui/Timestamp'
import { useToast } from '../components/ui/Toast'
import { useUserTimezone } from '../hooks/useUserTimezone'
import { DreamService, type Dream, type DreamBoardCard, type DreamJourneyPlan } from '../services/dreamService'
import { DreamGapsCard } from '../components/dreams/DreamGapsCard'
import { DreamKeepingUp } from '../components/dreams/DreamKeepingUp'
import { DreamHoldMeToIt } from '../components/dreams/DreamHoldMeToIt'
import { DreamJourney } from '../components/dreams/DreamJourney'
import { DreamAddRow, DreamAreasEditor, DreamPriorityEditor, DreamTargetDateEditor, DreamTitleEditor, DreamWhyEditor } from '../components/dreams/inline/DreamInlineEditors'
import { cleanupWords, nextWhenWords, nextWords, priorityWord, progressWords, todayWords } from '../utils/dreamWords'
import { SECTION_TITLE, dreamState, sectionOf, shownProgress, targetPassed } from '../utils/dreamSections'
import DreamGraphTabs from '../components/hierarchy/DreamGraphTabs'
import BlockersSection from '../components/blockers/BlockersSection'
import { OccurrencesSection } from '../components/occurrences/OccurrencesSection'

/**
 * The dream page (Dreams UX pass 2026-09-06), in the task page's frame:
 * breadcrumb → title with one summary line → the verbs → body | rail. Every
 * fact is edited where it is shown; the Add row offers what is not set yet;
 * Mark done has an Undo; pause, resume and let go are verbs. The rail carries
 * the canonical order — Fill the gaps · Plan · Hold me to it · Keeping up.
 *
 * A dream is a container of goals: its journey is goals, and the page reads
 * them. Nothing is scheduled, logged or reminded on the dream.
 */
const SECTION_HEADING = 'flex items-center gap-1.5 text-app-meta font-bold uppercase tracking-widest text-secondary'
const pad = (n: number) => String(n).padStart(2, '0')
const todayKey = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` }

export const DreamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { timezone } = useUserTimezone()
  const [searchParams, setSearchParams] = useSearchParams()
  const [dream, setDream] = useState<Dream | null>(null)
  const [card, setCard] = useState<DreamBoardCard | null>(null)
  const [plan, setPlan] = useState<DreamJourneyPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [letGoOpen, setLetGoOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [dateOpen, setDateOpen] = useState(0)
  const dreamService = useMemo(() => new DreamService(), [])
  const dreamNumericId = id ? parseInt(id, 10) : NaN
  const autoStart = searchParams.get('journey') === 'start' || searchParams.get('action') === 'breakdown'

  useProvideFeedbackContext({ pageType: 'dream_detail', entityType: 'dream', entityId: id, extra: { title: dream?.title, status: dream?.status } })

  useEffect(() => {
    if (!searchParams.get('action')) return
    const next = new URLSearchParams(searchParams); next.delete('action'); setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadCard = useCallback(async () => {
    if (!id) return
    try {
      const board = await dreamService.getBoard(todayKey())
      setCard(board.dreams[String(id)] || null)
    } catch { setCard(null) }
  }, [dreamService, id])

  const load = useCallback(async () => {
    if (!id) return
    try {
      setError(null)
      const data = await dreamService.getDream(id)
      setDream(data)
      void loadCard()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dream')
    } finally {
      setLoading(false)
    }
  }, [dreamService, id, loadCard])

  useEffect(() => { void load() }, [load])

  const onChanged = (d: Dream) => { setDream(d); void loadCard() }
  const onPlan = useCallback((p: DreamJourneyPlan | null) => { setPlan(p); void loadCard() }, [loadCard])

  // ── verbs ──
  const setStatus = async (status: 'completed' | 'active' | 'paused_by_user' | 'cancelled', words: { title: string; description?: string; undo?: boolean }, restore?: { status: string; progress: number }) => {
    if (!dream) return
    setBusy(status)
    try {
      const before = { status: dream.status, progress: dream.progress }
      await dreamService.updateDream(dream.id, restore ? { status: restore.status, progress: restore.progress } : status === 'completed' ? { status, progress: 100 } : { status })
      onChanged(await dreamService.getDream(dream.id))
      setLetGoOpen(false)
      toast.toast({ variant: status === 'completed' ? 'success' : 'info', title: words.title, description: words.description,
                    action: words.undo ? { label: 'Undo', onClick: () => void setStatus('active', { title: 'Back as it was' }, { status: before.status || 'active', progress: before.progress }) } : undefined })
    } catch (err) {
      toast.error('That did not go through', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const confirmDelete = async () => {
    if (!dream) return
    try {
      const result = await dreamService.deleteDream(dream.id)
      setDeleteOpen(false)
      toast.toast({ variant: 'success', title: 'Dream deleted', description: cleanupWords(result.cleanup) })
      navigate('/dreams')
    } catch (err) {
      setDeleteOpen(false)
      toast.error('Could not delete it', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  // ── frame ──
  if (loading) {
    return <div className="flex h-64 items-center justify-center"><RefreshCw className="h-6 w-6 animate-spin text-brand" /><span className="ml-3 text-secondary">Loading dream…</span></div>
  }
  if (error || !dream) {
    return (
      <div className="space-y-4">
        <Link to="/dreams" className="inline-flex items-center gap-1 text-app-meta text-muted hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Dreams</Link>
        <Card className="border-bad/30"><CardContent className="pt-6"><div className="py-8 text-center" role="alert">
          <AlertCircle className="mx-auto mb-4 h-12 w-12 text-bad" />
          <h2 className="mb-2 text-xl font-semibold text-primary">{error || 'This dream is not here'}</h2>
          <Button onClick={() => navigate('/dreams')}>Go to dreams</Button>
        </div></CardContent></Card>
      </div>
    )
  }

  const state = dreamState(dream)
  const finished = state.key === 'done' || state.key === 'let_go'
  const resting = state.key === 'paused'
  const live = !finished && !resting
  const stateDot = state.key === 'done' ? 'bg-ok' : finished || resting ? 'bg-muted' : card?.today ? 'bg-ok' : 'bg-brand'
  const passed = live && targetPassed(dream, todayKey())
  const pct = shownProgress(dream, card)
  const next = nextWords(card?.next)
  const nextWhen = nextWhenWords(card?.next, timezone)
  const nextHref = card?.next ? (card.next.kind === 'goal' ? `/goals/${card.next.id}` : `/${card.next.kind}s/${card.next.id}`) : null
  // The state word is the list's section word (North Star rule 2, 2026-09-12):
  // the page said "Open" over rows filed under "Needs a journey" and "Ready to check".
  const stateWords = state.words || (card?.today ? 'Something done today' : SECTION_TITLE[sectionOf(dream, card, todayKey())])
  // One filled button (rule 3): the next thing while live; Mark done once the
  // journey is complete; Plan the journey when there is nothing under it;
  // Resume while paused; the review banner's "It is done" when the date has passed.
  const journeyComplete = (plan && plan.status === 'completed') || card?.path?.status === 'completed'
  const doneLeads = live && Boolean(journeyComplete) && !passed
  const nextLeads = live && Boolean(card?.next && nextHref) && !journeyComplete && !passed
  const planLeads = live && !card?.next && !plan && !(card?.goals?.length) && !journeyComplete && !passed

  type Fact = { key: string; label: string; value: React.ReactNode }
  const facts: Fact[] = []
  if (dream.priority === 'high' || dream.priority === 'critical') facts.push({ key: 'priority', label: 'Priority', value: live ? <DreamPriorityEditor dream={dream} onChanged={onChanged} /> : priorityWord(dream.priority) })

  const menuItems = [
    ...(live ? [{ label: 'Let it go for now', icon: <XCircle className="h-4 w-4" />, onSelect: () => setLetGoOpen(true), testId: 'dream-let-go' }] : []),
    ...(finished ? [{ label: 'Reopen', icon: <Undo2 className="h-4 w-4" />, onSelect: () => void setStatus('active', { title: 'Back on', description: 'Pick up where you left off.' }), testId: 'dream-reopen' }] : []),
    { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onSelect: () => setDeleteOpen(true), danger: true, testId: 'dream-delete' },
  ]

  return (
    <div className="space-y-4">
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-app-meta text-muted" data-testid="dream-breadcrumb">
        <Link to="/dreams" className="inline-flex shrink-0 items-center gap-1 hover:text-brand"><ArrowLeft className="h-3.5 w-3.5" /> Dreams</Link>
      </nav>

      {passed && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand-subtle p-3" role="status" data-testid="dream-target-review">
          <span className="text-app-meta text-primary">The date you set for this has passed. How is it going?</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="xs" onClick={() => void setStatus('completed', { title: 'Done', description: dream.title, undo: true })} data-testid="dream-review-done"><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />It is done</Button>
            <Button size="xs" variant="outline" onClick={() => setDateOpen((n) => n + 1)} data-testid="dream-review-new-date">Still going · pick a new date</Button>
            <Button size="xs" variant="outline" onClick={() => setLetGoOpen(true)} data-testid="dream-review-let-go">Let it go for now</Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <CardTitle className={`text-xl leading-tight sm:text-2xl ${finished ? 'text-secondary' : 'text-primary'}`}>
              {live ? <DreamTitleEditor dream={dream} onChanged={onChanged} trigger={dream.title} /> : dream.title}
            </CardTitle>
            <div className="inline-flex flex-wrap items-center gap-x-1.5 text-app-meta text-secondary" data-testid="dream-summary-line">
              <span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${stateDot}`} aria-hidden="true" />{stateWords}</span>
              {!finished && progressWords(pct) && <span>· {progressWords(pct)}</span>}
              <span>· <DreamAreasEditor dream={dream} onChanged={onChanged} editable={live} /></span>
              {dream.target_date && <span>· {live ? <DreamTargetDateEditor dream={dream} onChanged={onChanged} openRequest={dateOpen} /> : <>by <Timestamp value={`${dream.target_date.slice(0, 10)}T12:00:00`} mode="date" /></>}</span>}
              {state.key === 'done' && dream.completed_at && <span className="text-ok">· done <Timestamp value={dream.completed_at} mode="datetime" /></span>}
              {live && card?.today && <span className="text-ok">· {todayWords(card.today)} {card.today.at ? <Timestamp value={card.today.at} mode="time" /> : 'today'}</span>}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="dream-actions">
            {live && card?.next && nextHref && (
              <Button variant={nextLeads ? 'default' : 'outline'} size="xs" className={nextLeads ? '' : 'hover:bg-brand-subtle'} onClick={() => navigate(nextHref)} data-testid="dream-next-step">
                {card.next.kind === 'habit' ? <Repeat className="mr-2 h-4 w-4" /> : card.next.kind === 'skill' ? <Wrench className="mr-2 h-4 w-4" /> : card.next.kind === 'goal' ? <Target className="mr-2 h-4 w-4" /> : <ExternalLink className="mr-2 h-4 w-4" />}
                {card.next.kind === 'goal' ? 'The goal to work on' : card.next.kind === 'habit' ? 'Next: the habit' : card.next.kind === 'skill' ? 'Next: the skill' : 'Next step'}
              </Button>
            )}
            {live && (
              <Button variant={doneLeads ? 'default' : 'outline'} size="xs" className={doneLeads ? '' : 'hover:bg-ok/10'} disabled={busy === 'completed'} onClick={() => void setStatus('completed', { title: 'Done', description: dream.title, undo: true })} data-testid="dream-done">
                <CheckCircle2 className="mr-2 h-4 w-4" />Mark done
              </Button>
            )}
            {live && (
              <Button variant="outline" size="xs" className="hover:bg-brand-subtle" disabled={busy === 'paused_by_user'} onClick={() => void setStatus('paused_by_user', { title: 'Paused', description: 'It waits here until you pick it back up. Nothing will nag you meanwhile.' })} data-testid="dream-pause">
                <Pause className="mr-2 h-4 w-4" />Pause
              </Button>
            )}
            {resting && (
              <Button size="xs" disabled={busy === 'active'} onClick={() => void setStatus('active', { title: 'Back on', description: 'Pick up where you left off.' })} data-testid="dream-resume">
                <Play className="mr-2 h-4 w-4" />Resume
              </Button>
            )}
            {finished && (
              <Button variant="outline" size="xs" className="hover:bg-brand-subtle" disabled={busy === 'active'} onClick={() => void setStatus('active', { title: 'Back on', description: 'Pick up where you left off.' })} data-testid="dream-reopen-button">
                <Undo2 className="mr-2 h-4 w-4" />Reopen
              </Button>
            )}
            <ActionMenu label={`More for ${dream.title}`} items={menuItems} testId="dream-more" />
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <div className="grid gap-6 border-t border-soft pt-4 lg:grid-cols-5">
            <div className="min-w-0 space-y-5 lg:col-span-3" data-testid="dream-body">
              <section data-testid="dream-about">
                <h3 className={SECTION_HEADING}><AlignLeft className="h-3.5 w-3.5 text-brand" /> About</h3>
                <p className="mt-2 text-sm text-primary" data-testid="dream-why">
                  {dream.description ? (live ? <DreamWhyEditor dream={dream} onChanged={onChanged} trigger={dream.description} /> : dream.description) : <span className="text-muted">Nothing written yet{live ? ' — the Add row below takes it' : ''}.</span>}
                </p>
                {facts.length > 0 && (
                  <dl className="mt-3 text-sm" data-testid="dream-facts">
                    {facts.map((f) => (
                      <div key={f.key} className="flex gap-3 py-1" data-testid={`dream-fact-${f.key}`}>
                        <dt className="w-28 shrink-0 pt-0.5 text-app-meta text-muted">{f.label}</dt>
                        <dd className="min-w-0 break-words text-primary">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {live && <div className="mt-3"><DreamAddRow dream={dream} onChanged={onChanged} /></div>}
              </section>

              <DreamJourney dream={dream} card={card} live={live} headingClass={SECTION_HEADING} tz={timezone} onPlan={onPlan} refreshKey={`${card?.open_goals ?? ''}:${card?.done_goals ?? ''}`} autoStart={autoStart} leads={planLeads} />

              {/* The Progress panel is gone (the plan's cut list, 2026-09-06): it showed
                  the stored figure under a Keeping up block showing the goals' average.
                  The log is just the log: Keeping up owns the count, the strip and the last time. */}
              <OccurrencesSection itemType="dream" itemId={dreamNumericId} refreshKey={`${dream.updated_at || ''}:${card?.today?.id || ''}:${card?.days_total ?? ''}`} headingClass={SECTION_HEADING} logOnly />

              {!Number.isNaN(dreamNumericId) && <BlockersSection itemType="dream" itemId={dreamNumericId} className="rounded-xl border border-soft bg-surface-alt p-4" />}
            </div>

            <aside className="min-w-0 space-y-4 rounded-xl bg-surface-alt p-4 lg:col-span-2" data-testid="dream-rail">
              {live && <DreamGapsCard dream={dream} onChanged={onChanged} embedded />}

              <div className={live ? 'border-t border-soft pt-4' : ''} data-testid="dream-plan">
                <h3 className={SECTION_HEADING}><Calendar className="h-3.5 w-3.5 text-brand" /> Plan</h3>
                <div className="mt-1.5 space-y-1 text-sm text-primary">
                  <div data-testid="dream-plan-next">
                    {card?.next && next && nextHref ? (
                      <span>Next: <Link to={nextHref} className="font-medium hover:text-brand hover:underline">{next.title}</Link>{nextWhen ? <span className="text-secondary"> · {nextWhen}</span> : null}{next.sub ? <span className="text-secondary"> · {next.sub}</span> : !nextWhen && card.next.kind !== 'goal' ? <span className="text-secondary"> · not on the calendar yet</span> : null}</span>
                    ) : plan && plan.status === 'completed' ? (
                      <span className="text-secondary">The journey is complete. Mark the dream done, or plan a new one.</span>
                    ) : (
                      <span className="text-secondary">{live ? 'No next step yet · plan the journey below' : 'Nothing planned'}</span>
                    )}
                  </div>
                  {/* Plan holds what is set; an unset by-when is offered once, in the Add
                      row and the gaps card. The editor stays mounted while a review
                      banner may open it. */}
                  {(dream.target_date || (live && passed)) && (
                    <div data-testid="dream-plan-by">
                      {live
                        ? <DreamTargetDateEditor dream={dream} onChanged={onChanged} openRequest={dateOpen} trigger={dream.target_date ? <>Get there by <Timestamp value={`${dream.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : 'Pick a new date'} />
                        : (dream.target_date ? <>Get there by <Timestamp value={`${dream.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : null)}
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-soft pt-4">
                <DreamHoldMeToIt card={card} headingClass={SECTION_HEADING} />
              </div>

              <div className="border-t border-soft pt-4">
                <DreamKeepingUp dream={dream} card={card} headingClass={SECTION_HEADING} live={live} onChanged={onChanged} />
              </div>
              <p className="text-app-micro text-muted">Added <Timestamp value={dream.created_at || ''} mode="date" />{dream.updated_at ? <> · changed <Timestamp value={dream.updated_at} mode="datetime" /></> : null}</p>
            </aside>
          </div>
        </CardContent>
      </Card>

      {dream?.id && <DreamGraphTabs nodeId={Number(dream.id)} nodeType="dream" />}

      <ConfirmationModal isOpen={letGoOpen} onClose={() => setLetGoOpen(false)} onConfirm={() => void setStatus('cancelled', { title: 'Let go for now', description: 'It stays in Done, and you can reopen it any time.' })} title="Let this dream go for now?"
                         message={`“${dream.title}” moves to Done as let go. Nothing under it is removed, and you can reopen it whenever it matters again.`} variant="warning" confirmText="Let it go" />
      <ConfirmationModal isOpen={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} title="Delete this dream?"
                         message={`“${dream.title}” will be removed, along with the journey goals not yet started. The other goals under it stay, on their own.`} variant="error" confirmText="Delete" />
    </div>
  )
}

export default DreamDetail
